import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./ServiceSelector.module.css";
import { musicPlayerActions } from "../../../store/musicPlayerSlice";
import { formatServiceLabel } from "../../../Hooks/catalogPresentation";

const ServiceSelector = (props) => {
  const dispatch = useDispatch();
  const { serviceSelected } = useSelector((state) => state.musicPlayer);
  const { catalog, catalogMetadata } = useSelector(
    (state) => state.catalogData,
  );

  const availableServices = useMemo(() => {
    const list = catalogMetadata?.availableServices;
    if (!Array.isArray(list)) return [];
    return list.filter(Boolean);
  }, [catalogMetadata]);

  const serviceNames = catalogMetadata?.availableServicesNamesObj || {};

  // Fallback when nothing is selected / stored value is no longer available.
  // Prefer remembered selection, then site default service, then first available.
  const fallbackService = useMemo(() => {
    if (!catalogMetadata || availableServices.length === 0) return null;

    if (serviceSelected && availableServices.includes(serviceSelected)) {
      return serviceSelected;
    }

    const defaultId = catalogMetadata.isDefaultService?.[1];
    if (defaultId && catalog?.[defaultId]?.slug) {
      const slug = catalog[defaultId].slug;
      if (availableServices.includes(slug)) return slug;
    }

    return availableServices[0];
  }, [availableServices, catalog, catalogMetadata, serviceSelected]);

  // Seed Redux from fallback only when empty or invalid; never overwrite a
  // valid remembered choice (localStorage → musicPlayer.serviceSelected).
  useEffect(() => {
    if (!fallbackService) return;
    if (!serviceSelected || !availableServices.includes(serviceSelected)) {
      dispatch(musicPlayerActions.updateServiceSelected(fallbackService));
    }
  }, [availableServices, fallbackService, dispatch, serviceSelected]);

  const activeService =
    serviceSelected && availableServices.includes(serviceSelected)
      ? serviceSelected
      : fallbackService;

  // Render order: keep the active service in the visual middle of the track.
  // Relative order of the remaining services is preserved.
  const displayServices = useMemo(() => {
    if (availableServices.length <= 1 || !activeService) {
      return availableServices;
    }
    const activeIndex = availableServices.indexOf(activeService);
    if (activeIndex < 0) return availableServices;

    const mid = Math.floor((availableServices.length - 1) / 2);
    if (activeIndex === mid) return availableServices;

    const next = availableServices.filter((s) => s !== activeService);
    next.splice(mid, 0, activeService);
    return next;
  }, [availableServices, activeService]);

  if (availableServices.length === 0) return null;

  const labelFor = (slug) =>
    serviceNames[slug] || formatServiceLabel(slug) || slug;

  const onSelect = (slug) => {
    if (!slug || slug === serviceSelected) return;
    dispatch(musicPlayerActions.updateServiceSelected(slug));
  };

  return (
    <div
      className={styles["service-selector"]}
      style={props.styles}
      role="tablist"
      aria-label="Streaming service"
    >
      {props.title && (
        <p className={styles["service-selector-title"]}>{props.title}</p>
      )}

      {displayServices.length === 1 ? (
        <p className={styles["service-single"]}>
          {labelFor(displayServices[0])}
        </p>
      ) : (
        <div className={styles["service-track"]}>
          {displayServices.map((service) => {
            const active = activeService === service;
            return (
              <button
                key={service}
                type="button"
                role="tab"
                aria-selected={active}
                value={service}
                className={
                  styles["service-option"] +
                  (active ? " " + styles["service-option-active"] : "")
                }
                onClick={() => onSelect(service)}
              >
                {labelFor(service)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ServiceSelector;
